import * as Sentry from '@sentry/react';

type Storage = 'local' | 'session';

const UNDEFINED_VALUE = '__undefined__';

const getStorage = (storage: Storage): globalThis.Storage =>
  window[storage === 'local' ? 'localStorage' : 'sessionStorage'];

export const writeStorage = <T>(
  key: string,
  value: T,
  storage: Storage = 'local'
): void => {
  try {
    const valueToWrite = typeof value === 'undefined' ? UNDEFINED_VALUE : value;
    getStorage(storage).setItem(key, JSON.stringify(valueToWrite));
  } catch (err) {
    Sentry.captureException(err);
    console.error(err);
  }
};

export const readStorage = <T>(
  key: string,
  initialState: T,
  storage: Storage = 'local'
): T => {
  try {
    const persistedStateJson = getStorage(storage).getItem(key);
    if (persistedStateJson === '"undefined"') {
      return undefined as T;
    }
    if (persistedStateJson) {
      const parsedValue = JSON.parse(persistedStateJson);

      if (parsedValue === UNDEFINED_VALUE) {
        return undefined as T;
      }

      return parsedValue;
    } else {
      if (initialState) {
        writeStorage(key, initialState);
      }
      return initialState;
    }
  } catch (err) {
    // Enhanced error logging with better context
    const persistedStateJson = getStorage(storage).getItem(key);
    const errorMessage = `Failed to parse ${storage}Storage key "${key}". Invalid JSON: ${persistedStateJson?.substring(
      0,
      100
    )}`;

    console.error(errorMessage, err);
    Sentry.captureException(err, {
      extra: {
        storageKey: key,
        storageType: storage,
        invalidValue: persistedStateJson,
        errorMessage,
      },
    });
    return initialState;
  }
};
